import React from "react";
import { FiCheckCircle, FiInfo, FiUserPlus, FiShield } from "react-icons/fi";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HotelLoader } from "../components/shared/HotelLoader";

const PRICING_PREVIEW = {
  basePrice: 499,
  pricePerRoom: 29.99,
  currency: "PLN",
  features: [
    "Access to all Guestivo modules",
    "Unlimited staff accounts",
    "Priority support via email and chat",
    "Advanced analytics and exports",
  ],
};

const RegisterPage: React.FC = () => {
  const { loginWithRedirect, isLoading, isAuthenticated } = useAuth0();
  const navigate = useNavigate();
  const { t } = useTranslation();

  React.useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate("/admin", { replace: true });
    }
  }, [isLoading, isAuthenticated, navigate]);

  if (isLoading) {
    return <HotelLoader message="Checking session" />;
  }

  if (isAuthenticated) return null;

  const handleContinue = async () => {
    await loginWithRedirect({
      authorizationParams: {
        screen_hint: "signup",
        redirect_uri: `${window.location.origin}/admin`,
        scope: "openid profile email",
      },
      appState: { returnTo: "/admin" },
    });
  };

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-10 md:py-16">
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold">
          Create a new hotel owner account
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          This registration creates an owner account for a new hotel in
          Guestivo. You'll set basic details and receive full admin permissions
          for this property. You can invite your team and manage roles later in
          settings.
        </p>

        <div className="bg-card border border-border rounded-lg p-5 md:p-6">
          <div className="space-y-4">
            <h2 className="text-lg font-medium flex items-center gap-2">
              <FiInfo className="text-blue-600 dark:text-blue-400" />
              About this registration
            </h2>
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-sm">
                <FiCheckCircle className="text-green-600 dark:text-green-400" />
                Creates an owner account for a new hotel property
              </li>
              <li className="flex items-center gap-2 text-sm">
                <FiCheckCircle className="text-green-600 dark:text-green-400" />
                Set hotel basics during onboarding (name, default locale,
                currency)
              </li>
              <li className="flex items-center gap-2 text-sm">
                <FiCheckCircle className="text-green-600 dark:text-green-400" />
                Full admin permissions for this hotel; invite staff and assign
                roles later
              </li>
              <li className="flex items-center gap-2 text-sm">
                <FiCheckCircle className="text-green-600 dark:text-green-400" />
                Secure sign up is handled by Auth0 (email/SSO where enabled)
              </li>
            </ul>
            <div className="flex flex-col md:flex-row gap-3 pt-2">
              <button
                onClick={handleContinue}
                disabled={isLoading}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                <FiUserPlus className="h-4 w-4" />
                Continue (login or create account)
              </button>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-5 md:p-6">
          <div className="space-y-4">
            <h2 className="text-lg font-medium flex items-center gap-2">
              <FiShield className="text-blue-600 dark:text-blue-400" />
              Pricing overview
            </h2>
            <p className="text-sm text-muted-foreground">
              Transparent pricing: {PRICING_PREVIEW.basePrice}{" "}
              {PRICING_PREVIEW.currency} base + {PRICING_PREVIEW.pricePerRoom}{" "}
              {PRICING_PREVIEW.currency} per room each month.
            </p>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-md border p-4">
                <p className="text-sm text-muted-foreground">Base fee</p>
                <p className="text-2xl font-semibold">
                  {PRICING_PREVIEW.basePrice} {PRICING_PREVIEW.currency}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Includes platform modules, support, and onboarding.
                </p>
              </div>
              <div className="rounded-md border p-4">
                <p className="text-sm text-muted-foreground">Per room</p>
                <p className="text-2xl font-semibold">
                  {PRICING_PREVIEW.pricePerRoom} {PRICING_PREVIEW.currency}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Only pay for the rooms you manage.
                </p>
              </div>
            </div>
            <ul className="space-y-2 text-sm">
              {PRICING_PREVIEW.features.map((key) => (
                <li key={t(key, key)} className="flex items-center gap-2">
                  <FiCheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                  {t(key, key)}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <p className="text-xs text-gray-500 dark:text-gray-400">
          By continuing, you agree to our terms and privacy policy.
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
